import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Message } from '@/types/chat';
import { useToast } from '@/hooks/use-toast';

export function useHelpAssistant() {
  const [messages, setMessages] = useState<Message[]>([{
    id: 'initial',
    role: 'assistant',
    content: 'Hei! Jeg er din hjelpeassistent. Hvordan kan jeg hjelpe deg i dag?',
    created_at: new Date().toISOString()
  }]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const sendMessage = async (content: string) => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        toast({
          variant: "destructive",
          title: "Autentiseringsfeil",
          description: "Vennligst logg inn på nytt."
        });
        return;
      }

      setIsLoading(true);

      // Add user message
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);

      // Get help context - using plain text search instead of full-text search
      const { data: helpData } = await supabase
        .from('faqs')
        .select('question, answer')
        .ilike('question', `%${content}%`)
        .limit(3);

      console.log('Help data retrieved:', helpData);

      // Get user context
      const { data: tokenData } = await supabase
        .from('token_allocations')
        .select('monthly_tokens, tokens_used')
        .eq('user_id', user.id)
        .single();

      const { data: casesData } = await supabase
        .from('cases')
        .select('title, status')
        .eq('user_id', user.id);

      // Generate response using edge function
      const { data, error } = await supabase.functions.invoke('help-assistant', {
        body: {
          message: content,
          context: {
            faqs: helpData || [],
            userTokens: tokenData,
            userCases: casesData || [],
          }
        }
      });

      if (error) throw error;

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.response,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error in help assistant:', error);
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Kunne ikke prosessere forespørselen. Vennligst prøv igjen senere."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    isLoading,
    sendMessage
  };
}